/**
 * Journey Persistence Service
 * Handles saving and loading journeys from both cookies (local) and Firebase (cloud)
 */

import { setChunked, getChunked, deleteChunked } from '@/utils/cookies';
import { db, auth } from '@/firebase/init';
import { doc, setDoc, getDoc, deleteDoc } from 'firebase/firestore';
import { cleanJourneyForStorage, validateJourney } from '@/models/JourneyModel';
import { deleteImagesByUrls, deleteJourneyImages } from '@/firebase/storageService';

const COOKIE_PREFIX = 'journey_';
const COOKIE_DAYS = 30;

/**
 * Save journey to cookies (local persistence)
 * @param {Object} journey - Journey object
 * @returns {boolean} Success status
 */
export function saveJourneyToCookies(journey) {
  try {
    if (!journey || !journey.journeyId) {
      console.warn('Cannot save invalid journey to cookies');
      return false;
    }
    
    // IMPORTANT: Don't save full journey to cookies (causes 431 error)
    // Only save a lightweight reference with minimal metadata
    const lightweightData = {
      journeyId: journey.journeyId,
      userId: journey.userId,
      updatedAt: journey.updatedAt || Date.now(),
      step: journey.step,
      status: journey.status,
      // Don't save setProducts - too large for cookies!
    };
    
    const cookieName = COOKIE_PREFIX + journey.journeyId;
    setChunked(cookieName, JSON.stringify(lightweightData), COOKIE_DAYS);
    
    console.log('Journey reference saved to cookies:', journey.journeyId);
    console.log('(Full data stored in Firebase only)');
    
    return true;
  } catch (error) {
    console.error('Error saving journey to cookies:', error);
    return false;
  }
}

/**
 * Load journey from cookies
 * @param {string} journeyId - Journey identifier
 * @returns {Object|null} Journey object or null
 */
export function loadJourneyFromCookies(journeyId) {
  try {
    if (!journeyId) return null;
    
    const cookieName = COOKIE_PREFIX + journeyId;
    const data = getChunked(cookieName);
    
    if (!data) return null;
    
    const journeyRef = JSON.parse(data);
    
    // Now cookies only store lightweight reference
    // Return the reference so caller knows to load from Firebase
    if (journeyRef && journeyRef.journeyId) {
      console.log('Journey reference found in cookies:', journeyId);
      return journeyRef;
    }
    
    return null;
  } catch (error) {
    console.error('Error loading journey reference from cookies:', error);
    return null;
  }
}

/**
 * Delete journey from cookies
 * @param {string} journeyId - Journey identifier
 */
export function deleteJourneyFromCookies(journeyId) {
  try {
    if (!journeyId) return;
    
    const cookieName = COOKIE_PREFIX + journeyId;
    deleteChunked(cookieName);
    
    console.log('Journey deleted from cookies:', journeyId);
  } catch (error) {
    console.error('Error deleting journey from cookies:', error);
  }
}

/**
 * Save journey to Firebase
 * @param {Object} journey - Journey object
 * @returns {Promise<boolean>} Success status
 */
export async function saveJourneyToFirebase(journey) {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.warn('User not authenticated, cannot save to Firebase');
      return false;
    }
    
    if (!journey || !journey.journeyId) {
      console.warn('Cannot save invalid journey to Firebase');
      return false;
    }
    
    const cleaned = cleanJourneyForStorage(journey);
    const journeyRef = doc(db, 'journeys', `${user.uid}_${journey.journeyId}`);
    
    const dataToSave = {
      ...cleaned,
      userId: user.uid,
      userEmail: user.email,
      updatedAt: Date.now()
    };
    
    await setDoc(journeyRef, dataToSave, { merge: true });
    
    console.log('Journey saved to Firebase:', journey.journeyId);
    return true;
  } catch (error) {
    console.error('Error saving journey to Firebase:', error);
    return false;
  }
}

/**
 * Load journey from Firebase
 * @param {string} journeyId - Journey identifier
 * @returns {Promise<Object|null>} Journey object or null
 */
export async function loadJourneyFromFirebase(journeyId) {
  try {
    const user = auth.currentUser;
    if (!user) return null;
    
    if (!journeyId) return null;
    
    const journeyRef = doc(db, 'journeys', `${user.uid}_${journeyId}`);
    const snap = await getDoc(journeyRef);
    
    if (!snap.exists()) return null;
    
    const journey = snap.data();
    
    if (!validateJourney(journey)) {
      console.warn('Invalid journey data in Firebase:', journeyId);
      return null;
    }
    
    // Ensure backward compatibility fields
    journey.isComplete = journey.isComplete !== undefined ? journey.isComplete : (journey.status === 'FINISHED');
    
    // Ensure all products have isComplete field and log personalization data
    if (Array.isArray(journey.setProducts)) {
      journey.setProducts = journey.setProducts.map(product => {
        // Log personalization data for debugging
        if (product.personalization && product.personalization.length > 0) {
          console.log(`Product "${product.name}" has ${product.personalization.length} personalization entries from Firebase:`, product.personalization);
        }
        
        return {
          ...product,
          isComplete: product.isComplete !== undefined ? product.isComplete : false
        };
      });
    }
    
    console.log('Journey loaded from Firebase:', journeyId);
    console.log(`- ${journey.setProducts?.length || 0} products with user data`);
    return journey;
  } catch (error) {
    console.error('Error loading journey from Firebase:', error);
    return null;
  }
}

/**
 * Delete journey from Firebase (including all images)
 * @param {string} journeyId - Journey identifier
 * @returns {Promise<boolean>} Success status
 */
export async function deleteJourneyFromFirebase(journeyId) {
  try {
    const user = auth.currentUser;
    if (!user) return false;
    
    if (!journeyId) return false;
    
    const journeyRef = doc(db, 'journeys', `${user.uid}_${journeyId}`);
    
    // Get journey data to find all images
    const journeySnap = await getDoc(journeyRef);
    
    if (journeySnap.exists()) {
      const journeyData = journeySnap.data();
      
      // Collect all image URLs from all products
      const imageUrls = [];
      
      if (Array.isArray(journeyData.setProducts)) {
        journeyData.setProducts.forEach(product => {
          if (Array.isArray(product.personalization)) {
            product.personalization.forEach(entry => {
              if (entry.type === 'pictures' && Array.isArray(entry.input)) {
                imageUrls.push(...entry.input.filter(url => typeof url === 'string'));
              }
            });
          }
        });
      }
      
      // Delete all images
      if (imageUrls.length > 0) {
        console.log(`Deleting ${imageUrls.length} images for journey ${journeyId}`);
        await deleteImagesByUrls(imageUrls);
      }
      
      // Also delete by folder path
      await deleteJourneyImages(user.uid, journeyId);
    }
    
    // Delete the journey document
    await deleteDoc(journeyRef);
    
    console.log('Journey deleted from Firebase:', journeyId);
    return true;
  } catch (error) {
    console.error('Error deleting journey from Firebase:', error);
    return false;
  }
}

/**
 * Sync journey to both cookies and Firebase
 * @param {Object} journey - Journey object
 * @returns {Promise<boolean>} Success status
 */
export async function syncJourney(journey) {
  try {
    // Save to cookies first (fast, synchronous)
    const cookieSuccess = saveJourneyToCookies(journey);
    
    // Then save to Firebase (slower, asynchronous)
    const firebaseSuccess = await saveJourneyToFirebase(journey);
    
    return cookieSuccess || firebaseSuccess;
  } catch (error) {
    console.error('Error syncing journey:', error);
    return false;
  }
}

/**
 * Load journey from cookies or Firebase (cookies first for speed)
 * @param {string} journeyId - Journey identifier
 * @returns {Promise<Object|null>} Journey object or null
 */
export async function loadJourney(journeyId) {
  try {
    // Try cookies first (fast) - but it only has reference now
    let cookieRef = loadJourneyFromCookies(journeyId);
    
    if (cookieRef && cookieRef.journeyId) {
      console.log('Journey reference found in cookies, loading full data from Firebase');
      
      // Load full data from Firebase
      const journey = await loadJourneyFromFirebase(journeyId);
      
      if (journey) {
        console.log('Full journey loaded from Firebase');
        return journey;
      }
    }
    
    // If no cookie reference, try Firebase directly
    const journey = await loadJourneyFromFirebase(journeyId);
    
    if (journey) {
      console.log('Journey loaded from Firebase');
      // Save lightweight reference to cookies for next time
      saveJourneyToCookies(journey);
      return journey;
    }
    
    return null;
  } catch (error) {
    console.error('Error loading journey:', error);
    return null;
  }
}

/**
 * Delete journey from both cookies and Firebase
 * @param {string} journeyId - Journey identifier
 * @returns {Promise<boolean>} Success status
 */
export async function deleteJourney(journeyId) {
  try {
    // Delete from cookies
    deleteJourneyFromCookies(journeyId);
    
    // Delete from Firebase
    await deleteJourneyFromFirebase(journeyId);
    
    console.log('Journey deleted from all storage:', journeyId);
    return true;
  } catch (error) {
    console.error('Error deleting journey:', error);
    return false;
  }
}

/**
 * Get all journey IDs from cookies
 * @returns {Array<string>} Array of journey IDs
 */
export function getAllJourneyIdsFromCookies() {
  try {
    const cookies = document.cookie.split(';');
    const journeyIds = new Set();
    
    cookies.forEach(cookie => {
      const name = cookie.trim().split('=')[0];
      if (name.startsWith(COOKIE_PREFIX)) {
        // Extract journey ID from cookie name
        const match = name.match(new RegExp(`^${COOKIE_PREFIX}(.+)_parts$`));
        if (match && match[1]) {
          journeyIds.add(match[1]);
        }
      }
    });
    
    return Array.from(journeyIds);
  } catch (error) {
    console.error('Error getting journey IDs from cookies:', error);
    return [];
  }
}

/**
 * Clean up all old journey cookies to prevent 431 error
 * Call this on app startup
 */
export function cleanupOldJourneyCookies() {
  try {
    const journeyIds = getAllJourneyIdsFromCookies();
    console.log(`Cleaning up ${journeyIds.length} old journey cookies...`);
    
    journeyIds.forEach(journeyId => {
      deleteJourneyFromCookies(journeyId);
    });
    
    console.log('Cookie cleanup complete');
  } catch (error) {
    console.error('Error cleaning up cookies:', error);
  }
}
